import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getParticipants } from '../../features/live-market/liveMarketUtils'

const GAMMA_SEARCH_URL = 'https://gamma-api.polymarket.com/public-search'
const GAMMA_EVENT_URL = 'https://gamma-api.polymarket.com/events/slug'
const GAMMA_EVENTS_URL = 'https://gamma-api.polymarket.com/events'
const POLYMARKET_WS_URL = 'wss://ws-subscriptions-clob.polymarket.com/ws/market'
const MARKET_CACHE_TTL_MS = 2 * 60 * 1000
const RENDER_INTERVAL_MS = 120
const HEARTBEAT_INTERVAL_MS = 10 * 1000
const DEFAULT_BOOK_DEPTH = 8
const SOCCER_TAG_ID = 100350
const MARKET_DEFS = [
  { id: 'moneyline', label: '1X2' },
  { id: 'totals', label: 'Over / Under 2.5' },
  { id: 'btts', label: 'BTTS' },
]
const TEAM_STOP_WORDS = new Set([
  'fc', 'cf', 'sc', 'ac', 'afc', 'fk', 'cd', 'ud', 'if', 'bk', 'pfc', 'pfk', 'club', 'football', 'futebol',
])

const normalizeText = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[æǽ]/g, 'ae')
    .replace(/œ/g, 'oe')
    .replace(/[øö]/g, 'o')
    .replace(/ł/g, 'l')
    .replace(/đ/g, 'd')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const getTeamTokens = (value) =>
  normalizeText(value)
    .split(' ')
    .filter((token) => token.length > 1 && !TEAM_STOP_WORDS.has(token))

const getEditDistance = (leftValue, rightValue) => {
  const left = String(leftValue || '')
  const right = String(rightValue || '')
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index)

  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    const current = [leftIndex]
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const substitution = previous[rightIndex - 1] + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1)
      current[rightIndex] = Math.min(previous[rightIndex] + 1, current[rightIndex - 1] + 1, substitution)
    }
    previous.splice(0, previous.length, ...current)
  }

  return previous[right.length]
}

const getTokenSimilarity = (left, right) => {
  if (left === right) return 1
  if (Math.min(left.length, right.length) >= 5 && (left.startsWith(right.slice(0, 5)) || right.startsWith(left.slice(0, 5)))) {
    return 0.88
  }
  if (Math.min(left.length, right.length) < 4) return 0
  const similarity = 1 - getEditDistance(left, right) / Math.max(left.length, right.length)
  return similarity >= 0.68 ? similarity : 0
}

const getTeamMatchScore = (text, teamName) => {
  const textTokens = getTeamTokens(text)
  const teamTokens = getTeamTokens(teamName)
  if (!teamTokens.length) return 0
  const score = teamTokens.reduce((total, teamToken) => {
    const bestTokenScore = textTokens.reduce(
      (best, textToken) => Math.max(best, getTokenSimilarity(teamToken, textToken)),
      0
    )
    return total + bestTokenScore
  }, 0)
  return score / teamTokens.length
}

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const getMatchKickoffMs = (match) => {
  const timestamp = Number(match?.starting_at_timestamp)
  if (Number.isFinite(timestamp) && timestamp > 0) return timestamp * 1000
  const parsed = Date.parse(String(match?.starting_at || '').replace(' ', 'T') + 'Z')
  return Number.isFinite(parsed) ? parsed : 0
}

const findBestEvent = (events, match) => {
  const { homeName, awayName } = getParticipants(match)
  const kickoffMs = getMatchKickoffMs(match)

  return (Array.isArray(events) ? events : [])
    .map((event) => {
      const homeScore = getTeamMatchScore(event?.title, homeName)
      const awayScore = getTeamMatchScore(event?.title, awayName)
      const eventStartMs = Date.parse(event?.startTime || event?.eventStartTime || event?.gameStartTime || event?.endDate || '')
      const timeDiffHours = kickoffMs && Number.isFinite(eventStartMs)
        ? Math.abs(eventStartMs - kickoffMs) / 3600000
        : 24
      const timeScore = timeDiffHours <= 3 ? 5 : timeDiffHours <= 12 ? 3 : timeDiffHours <= 36 ? 1 : -5
      const isSecondaryEvent = /more markets|exact score|half(time)? result|first (team )?to score|total corners/i.test(event?.title || '')
      const score = homeScore * 8 + awayScore * 8 + timeScore + (event?.active && !event?.closed ? 1 : 0) - (isSecondaryEvent ? 4 : 0)
      return { event, homeScore, awayScore, score, timeDiffHours }
    })
    .filter((candidate) =>
      candidate.homeScore >= 0.55 &&
      candidate.awayScore >= 0.55 &&
      candidate.timeDiffHours <= 48
    )
    .sort((left, right) => right.score - left.score)[0]?.event || null
}

const getCachedEvent = (matchId) => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(`filtermarket_poly_event_v2_${matchId}`) || 'null')
    if (cached?.event && Date.now() - Number(cached.savedAt) < MARKET_CACHE_TTL_MS) return cached.event
  } catch {
    return null
  }
  return null
}

const cacheEvent = (matchId, event) => {
  try {
    sessionStorage.setItem(`filtermarket_poly_event_v2_${matchId}`, JSON.stringify({ event, savedAt: Date.now() }))
  } catch (error) {
    // The orderbook still works when browser storage is unavailable.
  }
}

const fetchSearchEvents = async (query, signal) => {
  const searchUrl = new URL(GAMMA_SEARCH_URL)
  searchUrl.searchParams.set('q', query)
  searchUrl.searchParams.set('limit_per_type', '50')
  searchUrl.searchParams.set('keep_closed_markets', '1')
  const response = await fetch(searchUrl, { signal })
  if (!response.ok) throw new Error('A Polymarket não respondeu à pesquisa do jogo.')
  const data = await response.json()
  return Array.isArray(data?.events) ? data.events : []
}

const fetchSoccerFallback = async (match, signal) => {
  for (let offset = 0; offset < 500; offset += 100) {
    const eventsUrl = new URL(GAMMA_EVENTS_URL)
    eventsUrl.searchParams.set('tag_id', String(SOCCER_TAG_ID))
    eventsUrl.searchParams.set('active', 'true')
    eventsUrl.searchParams.set('closed', 'false')
    eventsUrl.searchParams.set('limit', '100')
    eventsUrl.searchParams.set('offset', String(offset))
    eventsUrl.searchParams.set('order', 'volume24hr')
    eventsUrl.searchParams.set('ascending', 'false')
    const response = await fetch(eventsUrl, { signal })
    if (!response.ok) break
    const events = await response.json()
    const selectedEvent = findBestEvent(events, match)
    if (selectedEvent) return selectedEvent
    if (!Array.isArray(events) || events.length < 100) break
  }
  return null
}

const mergeRelatedMarkets = async (primaryEvent, match, signal) => {
  if (!primaryEvent?.slug || primaryEvent.slug.endsWith('-more-markets')) return primaryEvent
  const relatedSlug = `${primaryEvent.slug}-more-markets`

  try {
    const response = await fetch(`${GAMMA_EVENT_URL}/${encodeURIComponent(relatedSlug)}`, { signal })
    if (!response.ok) return primaryEvent
    const relatedEvent = await response.json()
    if (!findBestEvent([relatedEvent], match)) return primaryEvent

    const marketsById = new Map()
    ;[...(primaryEvent.markets || []), ...(relatedEvent.markets || [])].forEach((market) => {
      if (market?.id) marketsById.set(String(market.id), market)
    })
    return {
      ...primaryEvent,
      markets: [...marketsById.values()],
      relatedEventSlug: relatedEvent.slug,
    }
  } catch (error) {
    if (error?.name === 'AbortError') throw error
    return primaryEvent
  }
}

const discoverPolymarketEvent = async (match, signal) => {
  const cachedEvent = getCachedEvent(match?.id)
  if (cachedEvent) return cachedEvent

  const { homeName, awayName } = getParticipants(match)
  const searchQueries = [...new Set([`${homeName} ${awayName}`, homeName, awayName].filter(Boolean))]
  const searchResults = await Promise.allSettled(searchQueries.map((query) => fetchSearchEvents(query, signal)))
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
  const searchEvents = searchResults.flatMap((result) => result.status === 'fulfilled' ? result.value : [])
  const deduplicatedEvents = [...new Map(searchEvents.map((event) => [String(event?.id || event?.slug), event])).values()]
  const selectedEvent = findBestEvent(deduplicatedEvents, match) || await fetchSoccerFallback(match, signal)
  if (!selectedEvent?.slug) return null

  const eventResponse = await fetch(`${GAMMA_EVENT_URL}/${encodeURIComponent(selectedEvent.slug)}`, { signal })
  if (!eventResponse.ok) throw new Error('Não foi possível carregar os mercados deste jogo.')
  const primaryEvent = await eventResponse.json()
  const fullEvent = await mergeRelatedMarkets(primaryEvent, match, signal)
  cacheEvent(match.id, fullEvent)
  return fullEvent
}

const getOutcomeDetails = (market) => {
  const outcomes = parseJsonArray(market?.outcomes)
  const tokenIds = parseJsonArray(market?.clobTokenIds)
  return outcomes
    .map((outcome, index) => ({ outcome, assetId: tokenIds[index] }))
    .filter((item) => item.assetId)
}

const makeOutcome = (market, item, label, order) => ({
  assetId: String(item.assetId),
  bestAsk: Number(market?.bestAsk) || null,
  bestBid: Number(market?.bestBid) || null,
  conditionId: market?.conditionId || market?.condition_id || '',
  label,
  lastTradePrice: Number(market?.lastTradePrice) || null,
  liquidity: Number(market?.liquidityNum ?? market?.liquidity) || 0,
  marketId: String(market?.id || ''),
  order,
  outcome: item.outcome,
  question: market?.question || '',
  volume: Number(market?.volumeNum ?? market?.volume) || 0,
})

const buildMarketGroups = (event, match) => {
  const { homeName, awayName } = getParticipants(match)
  const markets = (Array.isArray(event?.markets) ? event.markets : []).filter((market) =>
    market?.active && !market?.closed && market?.enableOrderBook !== false
  )

  const moneylineOutcomes = markets
    .filter((market) => market?.sportsMarketType === 'moneyline')
    .flatMap((market) => {
      const yesOutcome = getOutcomeDetails(market).find((item) => normalizeText(item.outcome) === 'yes')
      if (!yesOutcome) return []
      const marketLabel = market?.groupItemTitle || market?.question || ''
      if (normalizeText(marketLabel).includes('draw')) return [makeOutcome(market, yesOutcome, 'Empate', 1)]
      const homeScore = getTeamMatchScore(marketLabel, homeName)
      const awayScore = getTeamMatchScore(marketLabel, awayName)
      if (homeScore >= awayScore) return [makeOutcome(market, yesOutcome, homeName, 0)]
      return [makeOutcome(market, yesOutcome, awayName, 2)]
    })
    .sort((left, right) => left.order - right.order)

  const totalsMarket = markets.find((market) =>
    market?.sportsMarketType === 'totals' && Math.abs(Number(market?.line) - 2.5) < 0.001
  )
  const totalsOutcomes = totalsMarket
    ? getOutcomeDetails(totalsMarket).map((item, index) =>
        makeOutcome(totalsMarket, item, normalizeText(item.outcome) === 'over' ? 'Over 2.5' : 'Under 2.5', index)
      )
    : []

  const bttsMarket = markets.find((market) => market?.sportsMarketType === 'both_teams_to_score')
  const bttsOutcomes = bttsMarket
    ? getOutcomeDetails(bttsMarket).map((item, index) =>
        makeOutcome(bttsMarket, item, normalizeText(item.outcome) === 'yes' ? 'Sim' : 'Não', index)
      )
    : []

  return [
    { id: 'moneyline', label: '1X2', outcomes: moneylineOutcomes },
    { id: 'totals', label: 'Over / Under 2.5', outcomes: totalsOutcomes },
    { id: 'btts', label: 'BTTS', outcomes: bttsOutcomes },
  ]
}

const serializeBook = (book) => ({
  asks: [...book.asks.entries()]
    .map(([price, size]) => ({ price: Number(price), size: Number(size) }))
    .sort((left, right) => left.price - right.price),
  bids: [...book.bids.entries()]
    .map(([price, size]) => ({ price: Number(price), size: Number(size) }))
    .sort((left, right) => right.price - left.price),
  lastTradePrice: Number(book.lastTradePrice) || null,
  timestamp: book.timestamp || null,
})

const usePolymarketBooks = (assetIds) => {
  const assetKey = useMemo(() => [...new Set(assetIds)].sort().join(','), [assetIds])
  const [books, setBooks] = useState({})
  const [liveMatchedByMarket, setLiveMatchedByMarket] = useState({})
  const [lastUpdate, setLastUpdate] = useState(null)
  const [status, setStatus] = useState(assetKey ? 'connecting' : 'idle')
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!assetKey) {
      setBooks({})
      setStatus('idle')
      return undefined
    }

    const subscribedAssets = assetKey.split(',')
    const mutableBooks = new Map()
    const seenTrades = new Set()
    const matchedTotals = new Map()
    let socket = null
    let heartbeatTimer = null
    let reconnectTimer = null
    let renderTimer = null
    let reconnectAttempt = 0
    let stopped = false

    const ensureBook = (assetId) => {
      if (!mutableBooks.has(assetId)) {
        mutableBooks.set(assetId, { asks: new Map(), bids: new Map(), lastTradePrice: null, timestamp: null })
      }
      return mutableBooks.get(assetId)
    }

    const publish = () => {
      if (renderTimer) return
      renderTimer = setTimeout(() => {
        renderTimer = null
        const nextBooks = {}
        mutableBooks.forEach((book, assetId) => {
          nextBooks[assetId] = serializeBook(book)
        })
        setBooks(nextBooks)
        setLiveMatchedByMarket(Object.fromEntries(matchedTotals.entries()))
        setLastUpdate(Date.now())
      }, RENDER_INTERVAL_MS)
    }

    const applyBookSnapshot = (message) => {
      const assetId = String(message?.asset_id || '')
      if (!assetId) return
      const book = ensureBook(assetId)
      book.bids = new Map((message?.bids || []).map((level) => [String(level.price), Number(level.size) || 0]))
      book.asks = new Map((message?.asks || []).map((level) => [String(level.price), Number(level.size) || 0]))
      book.timestamp = message?.timestamp || null
      publish()
    }

    const applyPriceChanges = (message) => {
      ;(message?.price_changes || []).forEach((change) => {
        const assetId = String(change?.asset_id || '')
        if (!assetId) return
        const book = ensureBook(assetId)
        const levels = String(change?.side || '').toUpperCase() === 'BUY' ? book.bids : book.asks
        const price = String(change?.price || '')
        const size = Number(change?.size) || 0
        if (!price) return
        if (size <= 0) levels.delete(price)
        else levels.set(price, size)
        book.timestamp = message?.timestamp || book.timestamp
      })
      publish()
    }

    const applyLastTrade = (message) => {
      const assetId = String(message?.asset_id || '')
      if (!assetId) return
      const book = ensureBook(assetId)
      book.lastTradePrice = Number(message?.price) || book.lastTradePrice
      book.timestamp = message?.timestamp || book.timestamp

      const tradeKey = message?.transaction_hash || `${message?.market}:${message?.timestamp}:${message?.price}:${message?.size}`
      if (tradeKey && !seenTrades.has(tradeKey)) {
        seenTrades.add(tradeKey)
        const conditionId = String(message?.market || '')
        const notional = (Number(message?.price) || 0) * (Number(message?.size) || 0)
        if (conditionId && notional > 0) {
          matchedTotals.set(conditionId, (matchedTotals.get(conditionId) || 0) + notional)
        }
      }
      publish()
    }

    const handleMessage = (rawMessage) => {
      if (rawMessage === 'PONG' || rawMessage === 'PING') return
      let payload
      try {
        payload = JSON.parse(rawMessage)
      } catch (parseError) {
        return
      }
      const messages = Array.isArray(payload) ? payload : [payload]
      messages.forEach((message) => {
        if (message?.event_type === 'book') applyBookSnapshot(message)
        else if (message?.event_type === 'price_change') applyPriceChanges(message)
        else if (message?.event_type === 'last_trade_price') applyLastTrade(message)
      })
    }

    const connect = () => {
      if (stopped) return
      setStatus(reconnectAttempt ? 'reconnecting' : 'connecting')
      setError(null)
      socket = new WebSocket(POLYMARKET_WS_URL)

      socket.onopen = () => {
        reconnectAttempt = 0
        setStatus('connected')
        socket.send(JSON.stringify({ assets_ids: subscribedAssets, type: 'market' }))
        heartbeatTimer = setInterval(() => {
          if (socket?.readyState === WebSocket.OPEN) socket.send('PING')
        }, HEARTBEAT_INTERVAL_MS)
      }
      socket.onmessage = (event) => handleMessage(event.data)
      socket.onerror = () => setError('Falha momentânea na ligação ao book.')
      socket.onclose = () => {
        if (heartbeatTimer) clearInterval(heartbeatTimer)
        heartbeatTimer = null
        if (stopped) return
        reconnectAttempt += 1
        setStatus('reconnecting')
        const delay = Math.min(15000, 1000 * 2 ** Math.min(reconnectAttempt - 1, 4))
        reconnectTimer = setTimeout(connect, delay)
      }
    }

    setBooks({})
    setLiveMatchedByMarket({})
    connect()

    return () => {
      stopped = true
      if (heartbeatTimer) clearInterval(heartbeatTimer)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (renderTimer) clearTimeout(renderTimer)
      if (socket && socket.readyState < WebSocket.CLOSING) socket.close()
    }
  }, [assetKey])

  return { books, error, lastUpdate, liveMatchedByMarket, status }
}

const formatAmount = (value, prefix = '') => {
  const numericValue = Math.max(0, Number(value) || 0)
  return `${prefix}${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: numericValue < 100 ? 2 : 0,
    useGrouping: false,
  }).format(numericValue)}`
}

const formatPrice = (price) => {
  const numericPrice = Number(price)
  return Number.isFinite(numericPrice) && numericPrice > 0 ? `${(numericPrice * 100).toFixed(1)}¢` : '--'
}

const formatOdds = (price) => {
  const numericPrice = Number(price)
  if (!Number.isFinite(numericPrice) || numericPrice <= 0) return '--'
  return (1 / numericPrice).toFixed(numericPrice < 0.02 ? 0 : 2)
}

const getDisplayPrice = (book, outcome) => {
  if (Number(book?.lastTradePrice) > 0) return Number(book.lastTradePrice)
  if (Number(outcome?.lastTradePrice) > 0) return Number(outcome.lastTradePrice)
  const bestBid = Number(book?.bids?.[0]?.price) || Number(outcome?.bestBid)
  const bestAsk = Number(book?.asks?.[0]?.price) || Number(outcome?.bestAsk)
  if (bestBid > 0 && bestAsk > 0) return (bestBid + bestAsk) / 2
  return bestBid || bestAsk || null
}

const BookLevel = ({ level, side }) => {
  const value = Number(level?.price) * Number(level?.size)
  return (
    <div className={`poly-book-level ${side}`}>
      <span className="poly-level-buy">{side === 'bid' ? formatAmount(value, '$') : ''}</span>
      <span className="poly-level-price">
        <strong>{formatPrice(level?.price)}</strong>
        <small>{formatOdds(level?.price)}</small>
      </span>
      <span className="poly-level-sell">{side === 'ask' ? formatAmount(value, '$') : ''}</span>
    </div>
  )
}

const OutcomeLadder = ({ book, liveMatched, outcome, streamStatus }) => {
  const [expanded, setExpanded] = useState(false)
  const levelsRef = useRef(null)
  const spreadRef = useRef(null)
  const bookReady = Boolean(book)
  const allAsks = book?.asks || []
  const allBids = book?.bids || []
  const asks = (expanded ? allAsks : allAsks.slice(0, DEFAULT_BOOK_DEPTH)).slice().reverse()
  const bids = expanded ? allBids : allBids.slice(0, DEFAULT_BOOK_DEPTH)
  const bestBid = Number(allBids[0]?.price)
  const bestAsk = Number(allAsks[0]?.price)
  const spread = bestAsk > 0 && bestBid > 0 ? Math.max(0, bestAsk - bestBid) : null
  const displayPrice = getDisplayPrice(book, outcome)
  const matched = Number(outcome.volume) + Number(liveMatched || 0)
  const totalLevels = allAsks.length + allBids.length

  useEffect(() => {
    if (!expanded || !bookReady) return undefined
    const frame = window.requestAnimationFrame(() => {
      const levels = levelsRef.current
      const spreadElement = spreadRef.current
      if (!levels || !spreadElement) return
      levels.scrollTop = Math.max(0, spreadElement.offsetTop - levels.clientHeight / 2 + spreadElement.clientHeight / 2)
    })
    return () => window.cancelAnimationFrame(frame)
  }, [bookReady, expanded])

  return (
    <article className="poly-outcome-ladder">
      <header className="poly-outcome-head">
        <div>
          <span>OUTCOME</span>
          <strong>{outcome.label}</strong>
        </div>
        <div className="poly-outcome-price">
          <strong>{formatPrice(displayPrice)}</strong>
          <small>odd {formatOdds(displayPrice)}</small>
        </div>
      </header>
      <div className="poly-outcome-stats">
        <span>Matched <strong>{formatAmount(matched, '$')}</strong></span>
        <span>Liquidez <strong>{formatAmount(outcome.liquidity, '$')}</strong></span>
      </div>
      {totalLevels > DEFAULT_BOOK_DEPTH * 2 && (
        <button
          aria-expanded={expanded}
          className="poly-depth-toggle"
          onClick={() => setExpanded((current) => !current)}
          type="button"
        >
          <i className={`bi ${expanded ? 'bi-arrows-collapse' : 'bi-arrows-expand'}`} />
          {expanded ? 'VISTA COMPACTA' : `VER PROFUNDIDADE · ${totalLevels} PREÇOS`}
        </button>
      )}
      <div className="poly-book-column-head">
        <span>COMPRAR</span>
        <span>PREÇO · ODD</span>
        <span>VENDER</span>
      </div>
      <div className={`poly-book-levels ${expanded ? 'expanded' : ''}`} ref={levelsRef}>
        {!book && <div className="poly-book-waiting">{streamStatus === 'connected' ? 'A aguardar snapshot...' : 'A ligar ao mercado...'}</div>}
        {book && !asks.length && <div className="poly-book-empty-side">SEM ORDENS DE VENDA</div>}
        {asks.map((level) => <BookLevel key={`ask-${level.price}`} level={level} side="ask" />)}
        {book && (
          <div className="poly-book-spread" ref={spreadRef}>
            <span>SPREAD <b>{spread === null ? '--' : `${(spread * 100).toFixed(1)}¢`}</b></span>
            <strong>ODD {formatOdds(displayPrice)}</strong>
            <small>{formatPrice(displayPrice)} · ÚLTIMO {formatPrice(book.lastTradePrice || outcome.lastTradePrice)}</small>
          </div>
        )}
        {bids.map((level) => <BookLevel key={`bid-${level.price}`} level={level} side="bid" />)}
        {book && !bids.length && <div className="poly-book-empty-side">SEM ORDENS DE COMPRA</div>}
      </div>
    </article>
  )
}

const getConnectionCopy = (status) => {
  if (status === 'connected') return 'Ligado ao vivo'
  if (status === 'reconnecting') return 'A reconectar'
  if (status === 'connecting') return 'A ligar'
  return 'Sem subscrição'
}

const PolymarketOrderBook = ({ match }) => {
  const [event, setEvent] = useState(null)
  const [discoveryStatus, setDiscoveryStatus] = useState('loading')
  const [discoveryError, setDiscoveryError] = useState(null)
  const [retryKey, setRetryKey] = useState(0)
  const [activeMarket, setActiveMarket] = useState('moneyline')

  useEffect(() => {
    const controller = new AbortController()
    setDiscoveryStatus((currentStatus) => currentStatus === 'ready' ? currentStatus : 'loading')
    setDiscoveryError(null)
    discoverPolymarketEvent(match, controller.signal)
      .then((nextEvent) => {
        if (!nextEvent) {
          setDiscoveryStatus('missing')
          setEvent(null)
          return
        }
        setEvent(nextEvent)
        setDiscoveryStatus('ready')
      })
      .catch((requestError) => {
        if (requestError?.name === 'AbortError') return
        setDiscoveryStatus('error')
        setDiscoveryError(requestError?.message || 'Não foi possível consultar a Polymarket.')
      })
    return () => controller.abort()
  }, [match, retryKey])

  const marketGroups = useMemo(() => buildMarketGroups(event, match), [event, match])
  const assetIds = useMemo(
    () => marketGroups.flatMap((group) => group.outcomes.map((outcome) => outcome.assetId)),
    [marketGroups]
  )
  const stream = usePolymarketBooks(assetIds)
  const selectedGroup = marketGroups.find((group) => group.id === activeMarket) || marketGroups[0]

  useEffect(() => {
    if (!event) return
    const currentGroup = marketGroups.find((group) => group.id === activeMarket)
    if (currentGroup?.outcomes?.length) return
    const firstAvailable = marketGroups.find((group) => group.outcomes.length)
    if (firstAvailable) setActiveMarket(firstAvailable.id)
  }, [activeMarket, event, marketGroups])

  if (discoveryStatus === 'loading') {
    return <section className="poly-orderbook-shell poly-orderbook-state"><i className="poly-loading-ring" /><strong>A localizar este jogo na Polymarket...</strong></section>
  }

  if (discoveryStatus === 'missing') {
    return (
      <section className="poly-orderbook-shell poly-orderbook-state">
        <i className="bi bi-search" />
        <strong>Jogo não encontrado na Polymarket</strong>
        <span>O Radar continuará disponível, mas não existe um mercado Polymarket associado com segurança.</span>
        <button type="button" onClick={() => setRetryKey((value) => value + 1)}>TENTAR NOVAMENTE</button>
      </section>
    )
  }

  if (discoveryStatus === 'error') {
    return (
      <section className="poly-orderbook-shell poly-orderbook-state error">
        <i className="bi bi-wifi-off" />
        <strong>Ligação à Polymarket indisponível</strong>
        <span>{discoveryError}</span>
        <button type="button" onClick={() => setRetryKey((value) => value + 1)}>RECONECTAR</button>
      </section>
    )
  }

  return (
    <section className="poly-orderbook-shell">
      <header className="poly-orderbook-header">
        <div>
          <span className="poly-orderbook-eyebrow">POLYMARKET · CLOB</span>
          <strong>Orderbook ao vivo</strong>
          <small>{event?.title}</small>
        </div>
        <div className="poly-orderbook-connection">
          <span className={`poly-connection-dot ${stream.status}`} />
          <strong>{getConnectionCopy(stream.status)}</strong>
          <small>{stream.lastUpdate ? `Atualizado ${new Date(stream.lastUpdate).toLocaleTimeString('pt-PT')}` : 'A aguardar dados'}</small>
        </div>
      </header>

      <div className="poly-market-toolbar">
        <div className="poly-market-tabs">
          {MARKET_DEFS.map((marketDef) => {
            const group = marketGroups.find((item) => item.id === marketDef.id)
            const isAvailable = Boolean(group?.outcomes?.length)
            return (
              <button
                className={activeMarket === marketDef.id ? 'active' : ''}
                disabled={!isAvailable}
                key={marketDef.id}
                onClick={() => setActiveMarket(marketDef.id)}
                title={isAvailable ? `Abrir ${marketDef.label}` : 'Mercado não disponibilizado pela Polymarket neste jogo'}
                type="button"
              >
                {marketDef.label}
                <small>{isAvailable ? `${group.outcomes.length} outcomes` : 'indisponível'}</small>
              </button>
            )
          })}
        </div>
        <a href={`https://polymarket.com/event/${event?.slug}`} rel="noopener noreferrer" target="_blank">VER NA POLYMARKET <i className="bi bi-box-arrow-up-right" /></a>
      </div>

      {stream.error && <div className="poly-stream-warning"><i className="bi bi-exclamation-triangle" /> {stream.error} Reconexão automática ativa.</div>}

      {selectedGroup?.outcomes?.length ? (
        <div className="poly-ladders-scroll">
          <div className="poly-ladders-grid" style={{ '--poly-outcome-count': selectedGroup.outcomes.length }}>
            {selectedGroup.outcomes.map((outcome) => (
              <OutcomeLadder
                book={stream.books[outcome.assetId]}
                key={outcome.assetId}
                liveMatched={stream.liveMatchedByMarket[outcome.conditionId]}
                outcome={outcome}
                streamStatus={stream.status}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="poly-market-unavailable">Este mercado não está disponível para este jogo na Polymarket.</div>
      )}

      <footer className="poly-orderbook-footer">
        <span><i className="poly-footer-swatch buy" /> Compra</span>
        <span><i className="poly-footer-swatch sell" /> Venda</span>
        <span>Preço nativo em cêntimos · odd decimal calculada</span>
        <span>Visualização somente de leitura</span>
      </footer>
    </section>
  )
}

export default PolymarketOrderBook
